import {
  AdjustmentsHorizontalIcon,
  ArrowDownTrayIcon,
  BanknotesIcon,
  BellAlertIcon,
  BellSlashIcon,
  ClockIcon,
  CurrencyDollarIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { IntevalLabels, RepeatType } from 'components/Frequency/constants'
import { LoanSearchField } from 'components/GlobalLoanSearch/LoanSearchField'
import { LayoutLoading } from 'components/LayoutLoading'
import { TotalsRow } from 'components/TableTotals'
import { itemCountPerPage } from 'config'
import { getAssignedToAccountTypes } from 'config/user.type.constants'
import { useSharedLoan } from 'context/SharedLoanContext'
import { usePermissions } from 'hooks/usePermissions'
import { type Payee, type Suggestion, AddPayableModal } from 'pages/Loan/Servicing/PartyPayments/AddPayableModal'
import { confirmActivePayoff } from 'pages/Loan/Servicing/payoffGuard'
import { TaskAssigneeOptions } from 'pages/Tasks/constants'
import { AddTaskModal } from 'pages/Tasks/Modals/addTask'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  addNewLoanInvoice,
  deleteLoanInvoice,
  downloadInvoices,
  getAccountingAccount,
  getInternalUserEmails,
  getLoanInvoices,
  getLoanPayees,
  getTrustLedgerAdditionalInfo,
  openS3Document,
  updateLoanInvoice,
} from 'services'
import { Button, InputDateRange, Pagination, Tooltip } from 'stories/components'
import {
  confirm,
  formatDate,
  formatTime,
  getLoanDisplayNo,
  Num,
  openAuditLog,
  removeComma,
  renderHeader,
  thousandSeperator,
} from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { AddInvoice } from './AddInvoice'
import { InvoiceFrequencyModal } from './InvoiceFrequencyModal'
import type { IInvoice } from './types'

export default function Invoice({ isLoan = true }: { isLoan?: any }) {
  const shared = useSharedLoan()
  const isShared = !!shared?.readOnly
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [data, setData] = useState<IInvoice[]>([])
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [editingData, setEditingData] = useState<IInvoice | null>(null)
  const [query, setQuery] = useState('')
  const [isGetUsersOnce, setIsGetUsersOnce] = useState(false)
  const [filters, setFilters] = useState({
    orderBy: 'createdAt',
    orderDir: '-1',
    isLoan: isLoan,
    pageNum: 0,
    status: 'All',
    paidStatus: 'All',
    account: '',
    dateFrom: '',
    dateTo: '',
    amountFrom: '',
    amountTo: '',
    count: itemCountPerPage,
  })
  const [amountInputs, setAmountInputs] = useState({ amountFrom: '', amountTo: '' })
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [totalAmount, setTotalAmount] = useState(0)
  const [approvedAmount, setApprovedAmount] = useState(0)
  const [invoiceNos, setInvoiceNos] = useState([])
  const [accountingAccounts, setAccountingAccounts] = useState<Record<number, any>>({})
  const [isShowAddTask, setShowAddTask] = useState(false)
  const [taskInvoiceItem, setTaskInvoiceItem] = useState<IInvoice | null>(null)
  const [frequencyInvoiceItem, setFrequencyInvoiceItem] = useState<IInvoice | null>(null)
  const [assignedToList, setAssignedToList] = useState<Record<string, string>>({})
  // Keyed by loan: the company-wide list shows invoices from many loans, and each one's payees are
  // its own. '' is the loan already in context on a loan's own Invoices screen.
  const [payees, setPayees] = useState<Record<string, Payee[]>>({})
  const [payPrefill, setPayPrefill] = useState<Partial<Suggestion> | null>(null)
  const [payLoanNumber, setPayLoanNumber] = useState<number | undefined>(undefined)

  const { hasPermission } = usePermissions()
  const canApproveInvoice = hasPermission('Loan.Servicing.Invoice.Approval')

  useEffect(() => {
    if (isShared) return
    setLoanNumber()
    getAccountingAccount().then((res) => {
      const rtl: Record<string, any> = {}
      res.map((item: any) => {
        rtl[item.id] = item.name
      })
      setAccountingAccounts(rtl)
    })
    getInternalUserEmails().then((emails) => {
      const newOptions: Record<string, string> = {}
      getAssignedToAccountTypes().forEach((key) => (newOptions[key] = TaskAssigneeOptions()[key]))
      emails.forEach((email) => (newOptions[email] = email))
      setAssignedToList(newOptions)
    })
  }, [isShared])

  useEffect(() => {
    if (isShared || !isGetUsersOnce) return
    const timeOutId = setTimeout(() => !loading && onChangeFilter('pageNum', 0), 700)
    return () => clearTimeout(timeOutId)
  }, [query])

  useEffect(() => {
    if (isShared || !isGetUsersOnce) return
    const timeOutId = setTimeout(() => {
      if (loading) return
      const newFilters: any = cloneDeep(filters)
      newFilters.amountFrom = amountInputs.amountFrom === '' ? '' : removeComma(amountInputs.amountFrom)
      newFilters.amountTo = amountInputs.amountTo === '' ? '' : removeComma(amountInputs.amountTo)
      newFilters.pageNum = 0
      setFilters(newFilters)
    }, 700)
    return () => clearTimeout(timeOutId)
  }, [amountInputs])

  const hasAdvancedFilters = !!(filters.dateFrom || filters.dateTo || filters.amountFrom || filters.amountTo)

  useEffect(() => {
    if (isShared) {
      const res = shared.pageData as any
      setData(res?.results || [])
      setTotalCount(res?.totalCount || 0)
      setTotalAmount(res?.totalAmount || 0)
      setApprovedAmount(res?.approvedAmount || 0)
      setInvoiceNos(res?.invoiceNos || [])
      setIsGetUsersOnce(true)
      setLoading(false)
      return
    }
    refetch()
  }, [filters, isShared, shared?.pageKey, shared?.pageData])

  const buildRequestFilters = (newFilters: Record<string, any> | null = null) => {
    return { ...(newFilters || filters), query }
  }

  const refetch = (newFilters: Record<string, any> | null = null) => {
    if (isShared) return
    setLoading(true)
    getLoanInvoices(buildRequestFilters(newFilters))
      .then((data) => {
        setIsGetUsersOnce(true)
        setData(data.results)
        setTotalCount(data.totalCount)
        setTotalAmount(data.totalAmount)
        setApprovedAmount(data.approvedAmount)
        setInvoiceNos(data.invoiceNos)
      })
      .finally(() => setLoading(false))
  }

  const onChangeFilter = (key: string, value: any) => {
    const newFilters: any = cloneDeep(filters)
    newFilters[key] = value
    newFilters.pageNum = 0
    setFilters(newFilters)
  }

  const onChangeDateRange = (value: { from?: string; to?: string }) => {
    const newFilters: any = cloneDeep(filters)
    newFilters.dateFrom = value?.from || ''
    newFilters.dateTo = value?.to || ''
    newFilters.pageNum = 0
    setFilters(newFilters)
  }

  const onChangeAmountInput = (key: 'amountFrom' | 'amountTo', value: any) => {
    setAmountInputs((prev) => ({ ...prev, [key]: value }))
  }

  const onClearAdvancedFilters = () => {
    setAmountInputs({ amountFrom: '', amountTo: '' })
    const newFilters: any = cloneDeep(filters)
    newFilters.dateFrom = ''
    newFilters.dateTo = ''
    newFilters.amountFrom = ''
    newFilters.amountTo = ''
    newFilters.pageNum = 0
    setFilters(newFilters)
  }

  const onAdd = () => {
    setEditingData(null)
    setIsOpen(true)
    setSelectedIndex(-1)
  }

  const onCloseModal = (isNeedRefetch: boolean) => {
    setIsOpen(false)
    setEditingData(null)
    if (isNeedRefetch) refetch()
  }

  const onEdit = (id: number, index: number) => {
    setEditingData(data[index])
    setIsOpen(true)
    setSelectedIndex(index)
  }

  const onSubmit = async (newData: Record<string, any>) => {
    if (selectedIndex == -1) {
      await addNewLoanInvoice(newData)
    } else {
      const item = data[selectedIndex]
      await updateLoanInvoice(item.id, newData)
    }
    setIsOpen(false)
    setSelectedIndex(-1)
    refetch()
  }

  const onDelete = async (id: number, index: number) => {
    const content = (
      <div className="mb-4 text-[18px]">
        Do you want to remove this item?
        <br />
        No. {index + 1}
      </div>
    )
    const result = await confirm(content)
    if (!result) return
    setLoading(true)
    await deleteLoanInvoice(id)
    refetch()
  }

  const onAddTask = (item: IInvoice) => {
    setTaskInvoiceItem(item)
    setShowAddTask(true)
  }

  const onEditFrequency = (item: IInvoice) => {
    setFrequencyInvoiceItem(item)
  }

  /**
   * Raise this invoice as a payment to its payee. The invoice already says who is owed, how much
   * and under which number, so Pay a Party opens with those filled in and the invoice number as
   * the reason. The payable carries the invoice as its source, so the same invoice queued twice
   * reads as that rather than as two unrelated payments.
   */
  const onPayParty = async (item: IInvoice) => {
    // A payment already sitting in the check queue is the payment. Raising another would cut the
    // same invoice twice.
    if ((Number(item.queuedPayableAmount) || 0) > 0) return
    // On a loan's own Invoices screen the loan is already in context. The company-wide list is not
    // pointed at any loan, so every lookup and the payment itself are scoped to the invoice's own,
    // and to 0 for an invoice that was never attached to a loan at all. A bill still has to be paid
    // whether or not a loan number was ever assigned to it, so 0 goes through the same queue; what
    // it can't have is loan parties or a payoff to invalidate.
    const loanNumber = isLoan ? undefined : Number(item.loanNumber) || 0
    const key = `${loanNumber ?? ''}`
    setLoading(true)
    try {
      // Same warning the Party Payments screen gives: a payment raised after a payoff's good
      // through date invalidates the quoted figure. There is no payoff without a loan.
      if (loanNumber !== 0) {
        const info: any = await getTrustLedgerAdditionalInfo(loanNumber).catch(() => null)
        if (!(await confirmActivePayoff(info?.payoffDate))) return
      }
      if (!payees[key]) {
        const res: any = await getLoanPayees(loanNumber).catch(() => null)
        setPayees((prev) => ({ ...prev, [key]: Array.isArray(res?.payees) ? res.payees : [] }))
      }
    } finally {
      setLoading(false)
    }
    setPayLoanNumber(loanNumber)
    setPayPrefill({
      sourceType: 'invoice',
      sourceId: item.id,
      payeeName: item.payee,
      amount: Number(item.amount) || 0,
      memo: item.no,
    })
  }

  const onCloseTaskModal = () => {
    setShowAddTask(false)
    setTaskInvoiceItem(null)
  }

  const onUpdateApproval = async (id: number, approved: boolean) => {
    const content = `Are you sure you want to ${approved ? 'approved' : 'disapprove'} this invoice?`
    const result = await confirm(content)
    if (!result) return
    setLoading(true)
    await updateLoanInvoice(id, { approved })
    refetch()
  }

  const onSort = (key: string, dir: number) => {
    const newFilters = cloneDeep(filters)
    newFilters['orderBy'] = key
    newFilters['orderDir'] = `${dir}`
    setFilters(newFilters)
  }

  const onPageNavigate = (num: number) => {
    const newFilters = cloneDeep(filters)
    newFilters.pageNum = num
    setFilters(newFilters)
  }

  const onDownloadCSV = () => {
    setLoading(true)

    downloadInvoices(buildRequestFilters()).finally(() => setLoading(false))
  }

  const sortableHeaders = [
    { title: 'Loan Number', key: 'loanNumber' },
    { title: 'Invoice Date', key: 'date' },
    { title: 'Due Date', key: 'dueDate' },
    { title: 'Payee', key: 'payee' },
    { title: 'No', key: 'no' },
    { title: 'Amount', key: 'amount' },
    { title: 'Is Paid', key: 'isPaid' },
    { title: 'Created At', key: 'createdAt' },
  ]

  const showHistory = async (id: number, keys: Array<string>) => {
    const options = {
      table: 'Invoice',
      field: keys.join(', '),
      keys: {
        field: keys,
        id,
      },
    }
    keys = keys.filter((key) => key)
    openAuditLog(options)
  }

  return (
    <div className={`invoices-container`}>
      <div className="max-w-screen-2xl m-auto">
        <div className={`relative bg-white`}>
          <h2 className="text-2xl font-bold flex items-center mb-5">Invoices</h2>
          <LayoutLoading show={loading} />
          <div className="flex flex-wrap gap-3 my-2 justify-between items-end">
            <div className="flex flex-wrap gap-3 items-end flex-1">
              <div className="w-64">
                <LoanSearchField title="Search" value={query} onChange={setQuery} />
              </div>
              <div className="w-48">
                <RenderInput
                  input={{
                    inputType: 'select',
                    title: 'Account',
                    options: accountingAccounts,
                    hasDefaultOption: true,
                    defaultOptionText: 'All',
                    value: filters.account,
                  }}
                  Key="account"
                  onChange={(key: string, value: any) => onChangeFilter(key, value)}
                />
              </div>
              <div className="w-44">
                <RenderInput
                  input={{
                    inputType: 'select',
                    title: 'Status',
                    options: ['All', 'Approved', 'Not Approved', 'Rejected'],
                    hasDefaultOption: true,
                    value: filters.status,
                  }}
                  Key="status"
                  onChange={(key: string, value: any) => onChangeFilter(key, value)}
                />
              </div>
              <div className="w-40">
                <RenderInput
                  input={{
                    inputType: 'select',
                    title: 'Paid Status',
                    options: ['All', 'Paid', 'Not Paid'],
                    hasDefaultOption: true,
                    value: filters.paidStatus,
                  }}
                  Key="paidStatus"
                  onChange={(key: string, value: any) => onChangeFilter(key, value)}
                />
              </div>
              <button
                type="button"
                className={`flex items-center gap-1.5 text-sm px-2 py-2 rounded hover:bg-gray-50 ${
                  showMoreFilters || hasAdvancedFilters ? 'text-shade-blue' : 'text-gray-600'
                }`}
                onClick={() => setShowMoreFilters((v) => !v)}
              >
                <AdjustmentsHorizontalIcon className="w-4 h-4" />
                {showMoreFilters ? 'Hide filters' : 'More filters'}
                {hasAdvancedFilters && !showMoreFilters && (
                  <span className="ml-0.5 w-1.5 h-1.5 rounded-full bg-shade-blue" />
                )}
              </button>
            </div>
            <div className="flex items-end gap-4">
              <div className="text-sm text-right leading-5 whitespace-nowrap">
                <p>
                  Total Amount: <span className="font-semibold">${thousandSeperator(totalAmount)}</span>
                </p>
                <p>
                  Approved to Pay: <span className="font-semibold">${thousandSeperator(approvedAmount)}</span>
                </p>
              </div>
              {!isShared && (
                <Button onClick={onAdd} className="px-[36px]">
                  Add
                </Button>
              )}
            </div>
          </div>

          {showMoreFilters && (
            <div className="flex flex-wrap gap-3 items-end mt-1 mb-2 p-3 bg-gray-50 rounded-lg border border-gray-100">
              <div className="min-w-[240px]">
                <InputDateRange
                  title="Date Range"
                  value={{ from: filters.dateFrom, to: filters.dateTo }}
                  onChange={onChangeDateRange}
                />
              </div>
              <div className="w-36">
                <RenderInput
                  input={{
                    inputType: 'text',
                    type: 'thousandSep',
                    title: 'Payment From',
                    prefix: '$',
                    value: amountInputs.amountFrom,
                  }}
                  Key="amountFrom"
                  onChange={(_key: string, value: any) => onChangeAmountInput('amountFrom', value)}
                />
              </div>
              <div className="w-36">
                <RenderInput
                  input={{
                    inputType: 'text',
                    type: 'thousandSep',
                    title: 'Payment To',
                    prefix: '$',
                    value: amountInputs.amountTo,
                  }}
                  Key="amountTo"
                  onChange={(_key: string, value: any) => onChangeAmountInput('amountTo', value)}
                />
              </div>
              {hasAdvancedFilters && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-shade-blue px-2 py-2"
                  onClick={onClearAdvancedFilters}
                >
                  <XMarkIcon className="w-4 h-4" />
                  Clear
                </button>
              )}
            </div>
          )}

          <div className="parties-container overflow-auto max-h-[70vh] mb-6 mt-6 shadow-md sm:rounded-lg">
            <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
              <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-6 py-3 sticky top-0 z-20 bg-slate-50 dark:bg-gray-700">
                    Actions
                  </th>
                  <th className="px-6 py-3 sticky top-0 z-20 bg-slate-50 dark:bg-gray-700">Approval</th>
                  <th scope="col" className="px-6 py-3 sticky top-0 z-20 bg-slate-50 dark:bg-gray-700">
                    No
                  </th>
                  {sortableHeaders
                    .filter((item) => !isLoan || item.key !== 'loanNumber')
                    .map(({ title, key }, index) =>
                      renderHeader({
                        className: 'sticky top-0 z-20 bg-slate-50 dark:bg-gray-700',
                        title,
                        sortable: true,
                        key,
                        sortOrder: filters.orderBy == key ? parseInt(filters.orderDir) : 0,
                        index,
                        onSort: (key: string, dir: number) => onSort(key, dir),
                      }),
                    )}
                  <th scope="col" className="px-6 py-3 sticky top-0 z-20 bg-slate-50 dark:bg-gray-700">
                    Files
                  </th>
                </tr>
              </thead>
              <tbody className="text-[14.5px] text-gray-900">
                {data.map((item, index) => {
                  // The status is stored as the label the Paid selector shows; an invoice already
                  // settled or rejected should not be queued for payment.
                  const isSettled = item.isPaid === true || ['Paid', 'Rejected'].includes(String(item.isPaid))
                  const inCheckQueue = (Number(item.queuedPayableAmount) || 0) > 0
                  return (
                    <tr key={index} className={`border-b ${index % 2 ? 'bg-gray-50' : ''}`}>
                      <td className="px-5 py-3">
                        {isShared ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          <div className="flex gap-1">
                            <span
                              className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                              onClick={() => onEdit(item.id, index)}
                            >
                              <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
                            </span>
                            <span
                              className="text-red-800 cursor-pointer hover-shadow1 p-1 rounded"
                              onClick={() => onDelete(item.id, index)}
                            >
                              <TrashIcon className="w-4 h-4"></TrashIcon>
                            </span>
                            <Tooltip message="Add Task">
                              <span
                                className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                                onClick={() => onAddTask(item)}
                              >
                                <PlusCircleIcon className="w-4 h-4"></PlusCircleIcon>
                              </span>
                            </Tooltip>
                            {!isSettled &&
                              (inCheckQueue ? (
                                <Tooltip message="Already in the check queue">
                                  <span className="text-gray-300 p-1 rounded">
                                    <CurrencyDollarIcon className="w-4 h-4" />
                                  </span>
                                </Tooltip>
                              ) : (
                                <Tooltip message="Pay a Party">
                                  <span
                                    className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                                    onClick={() => onPayParty(item)}
                                  >
                                    <CurrencyDollarIcon className="w-4 h-4"></CurrencyDollarIcon>
                                  </span>
                                </Tooltip>
                              ))}
                            <Tooltip
                              message={
                                item.frequency?.repeatType === RepeatType.Repeat
                                  ? `${IntevalLabels[item.frequency.intervalUnit] || 'Recurring'} — click to edit`
                                  : 'Set recurring schedule'
                              }
                            >
                              <span
                                className="cursor-pointer hover-shadow1 p-1 rounded"
                                onClick={() => onEditFrequency(item)}
                              >
                                {item.frequency?.repeatType === RepeatType.Repeat ? (
                                  <BellAlertIcon className="text-shade-blue w-4 h-4" />
                                ) : (
                                  <BellSlashIcon className="w-4 h-4 text-gray-400" />
                                )}
                              </span>
                            </Tooltip>
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-3 flex justify-center gap-2 items-center">
                        {isShared ? (
                          item.approved ? (
                            <span className="text-green-500">Approved</span>
                          ) : (
                            <span className="text-shade-blue">Pending</span>
                          )
                        ) : canApproveInvoice ? (
                          <div
                            className={`text-white ${
                              item.approved ? 'bg-green-500' : 'bg-red-500'
                            } cursor-pointer hover-shadow1 p-1 rounded w-fit`}
                            onClick={() => onUpdateApproval(item.id, !item.approved)}
                          >
                            <BanknotesIcon className="w-4 h-4" />
                          </div>
                        ) : item.approved ? (
                          <span className="text-green-500">Approved</span>
                        ) : (
                          <span className="text-shade-blue">Pending</span>
                        )}
                        {!isShared && (
                          <span className="ml-2 text-gray-500 cursor-pointer">
                            <ClockIcon className="w-4 h-4" onClick={() => showHistory(item.id, ['approved'])} />
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3">{index + 1 + itemCountPerPage * filters.pageNum}</td>
                      {!isLoan && (
                        <td className="px-2 py-3">
                          <Link
                            to={`/loan_process/overview/${item.loanNumber}`}
                            target="_blank"
                            className="text-shade-blue hover:underline cursor-pointer font-bold dark:text-white whitespace-nowrap flex flex-col"
                          >
                            {getLoanDisplayNo({
                              loanNumber: item.loanNumber,
                              byteproFileName: item.byteproFileName,
                            })}
                          </Link>
                        </td>
                      )}
                      <td className="px-2 py-3">{formatDate(`${item.date}`.substring(0, 10))}</td>
                      <td className="px-2 py-3">
                        {item.dueDate ? formatDate(`${item.dueDate}`.substring(0, 10)) : ''}
                      </td>
                      <td className="px-2 py-3">{item.payee}</td>
                      <td className="px-2 py-3">{item.no}</td>
                      <td className="px-2 py-3">{thousandSeperator(item.amount)}</td>
                      <td className="px-2 py-3">{item.isPaid}</td>
                      <td className="px-2 py-3">{formatTime(item.createdAt)}</td>
                      <td className="px-2 py-3">
                        {item.files.map((file) => (
                          <p
                            className="flex hover:underline items-center rounded text-shade-blue cursor-pointer"
                            onClick={() => openS3Document(file.fileKey)}
                          >
                            <span className="p-1">
                              <ArrowDownTrayIcon className="w-4 h-4" />
                            </span>
                            {file.name}
                          </p>
                        ))}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              {data.length > 0 && (
                <TotalsRow
                  tdClass="px-2 py-3"
                  cells={[
                    { value: 'Totals', className: 'px-6 py-3' },
                    '',
                    '',
                    ...(!isLoan ? [''] : []),
                    '', // Invoice Date
                    '', // Due Date
                    '', // Payee
                    '', // No
                    // From the server: the screen is paged, and this is the same figure as the
                    // "Total Amount" headline above the grid.
                    { value: Num(totalAmount), className: 'px-2 py-3' },
                    '', // Is Paid
                    '', // Created At
                    '', // Files
                  ]}
                />
              )}
            </table>
          </div>
          {(!isLoan || !isShared) && (
            <div className="flex justify-end items-center mt-3 mb-3">
              {!isLoan && (
                <Pagination
                  totalCount={totalCount}
                  itemCountPerPage={itemCountPerPage}
                  onNavigate={onPageNavigate}
                  pageNum={filters.pageNum}
                />
              )}
              {!isShared && (
                <Tooltip message="Download CSV">
                  <span
                    className="mt-1 p-2 text-sm cursor-pointer text-shade-blue hover-shadow1 rounded"
                    onClick={() => onDownloadCSV()}
                  >
                    <div className="flex gap-2 hover:underline">
                      <ArrowDownTrayIcon className="w-4 h-4" />
                    </div>
                  </span>
                </Tooltip>
              )}
            </div>
          )}
        </div>
      </div>
      {!isShared && isOpen && (
        <AddInvoice
          onUpdate={onSubmit}
          onClose={onCloseModal}
          item={editingData}
          index={selectedIndex}
          isLoan={isLoan}
          accountingAccounts={accountingAccounts}
          invoiceNos={invoiceNos}
        />
      )}
      {!isShared && isShowAddTask && taskInvoiceItem && (
        <AddTaskModal
          onClose={onCloseTaskModal}
          projectId={0}
          loanNumber={taskInvoiceItem.loanNumber}
          byteproFileName={taskInvoiceItem.byteproFileName}
          assignedToList={assignedToList}
          task={null}
          defaultData={{
            description: 'Invoice',
            note: `Invoice - Payee: ${taskInvoiceItem.payee}, Amount: $${thousandSeperator(
              taskInvoiceItem.amount,
            )}, Loan #: ${getLoanDisplayNo({
              loanNumber: taskInvoiceItem.loanNumber,
              byteproFileName: taskInvoiceItem.byteproFileName,
            })}`,
            files: taskInvoiceItem.files,
          }}
        />
      )}
      {!isShared && frequencyInvoiceItem && (
        <InvoiceFrequencyModal
          item={frequencyInvoiceItem}
          onClose={() => setFrequencyInvoiceItem(null)}
          onSaved={() => {
            setFrequencyInvoiceItem(null)
            refetch()
          }}
        />
      )}
      {!isShared && payPrefill && (
        <AddPayableModal
          payees={payees[`${payLoanNumber ?? ''}`] || []}
          prefill={payPrefill}
          loanNumber={payLoanNumber}
          onClose={() => setPayPrefill(null)}
          onDone={() => {
            setPayPrefill(null)
            refetch()
          }}
        />
      )}
    </div>
  )
}
